import { useRef, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Camera, Loader2, X, Upload, ScanLine, Check } from "lucide-react";

const AUTO_INTERVAL_MS = 2500; // how often we sample the live feed
const MAX_AUTO_ATTEMPTS = 24; // ~60s of auto-scanning, then ask to capture manually

function resizeToDataUrl(file, maxSize = 1400, quality = 0.82) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) { height = Math.round((height * maxSize) / width); width = maxSize; }
        else { width = Math.round((width * maxSize) / height); height = maxSize; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PlateScanner({ onResult, label = "Scan Plate", className = "", testid = "scan-plate-btn" }) {
  const inputRef = useRef(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const inFlight = useRef(false);
  const attempts = useRef(0);
  const [loading, setLoading] = useState(false);
  const [camOpen, setCamOpen] = useState(false);
  const [status, setStatus] = useState("");
  const [flash, setFlash] = useState(false);

  // Instant confirmation when a plate locks in: short buzz + beep + green flash.
  const signalLock = useCallback(() => {
    try { navigator.vibrate?.(180); } catch { /* not supported */ }
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) {
        const ac = new Ctx();
        const osc = ac.createOscillator();
        const gain = ac.createGain();
        osc.type = "sine";
        osc.frequency.value = 900;
        osc.connect(gain); gain.connect(ac.destination);
        gain.gain.setValueAtTime(0.0001, ac.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.32, ac.currentTime + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.0001, ac.currentTime + 0.28);
        osc.start();
        osc.stop(ac.currentTime + 0.3);
        osc.onended = () => { try { ac.close(); } catch { /* noop */ } };
      }
    } catch { /* audio unavailable */ }
    setFlash(true);
  }, []);

  const stopAuto = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  }, []);

  const stopCamera = useCallback(() => {
    stopAuto();
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    setCamOpen(false);
    setStatus("");
    setFlash(false);
  }, [stopAuto]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const grabFrame = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const canvas = document.createElement("canvas");
    const scaleW = Math.min(1400, v.videoWidth);
    canvas.width = scaleW;
    canvas.height = Math.round((v.videoHeight / v.videoWidth) * scaleW);
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.85);
  };

  // Single scan used by manual capture + photo upload.
  const scan = async (image) => {
    setLoading(true);
    try {
      const { data } = await api.post("/plate/scan", { image });
      if (!data.plate) toast.error("Couldn't read a plate — try a clearer, straight-on shot.");
      else signalLock();
      onResult?.(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Plate scan failed. Please try again.");
    } finally { setLoading(false); }
  };

  // Auto-scan tick: samples the live feed and returns a match the moment a plate is read.
  const tick = useCallback(async () => {
    if (inFlight.current) return;
    if (attempts.current >= MAX_AUTO_ATTEMPTS) {
      stopAuto();
      setStatus("No plate detected — move closer or tap Capture.");
      return;
    }
    const img = grabFrame();
    if (!img) return;
    attempts.current += 1;
    inFlight.current = true;
    setStatus("Scanning — hold steady on the plate…");
    try {
      const { data } = await api.post("/plate/scan", { image: img });
      if (data && data.plate) {
        stopAuto();
        signalLock();
        setStatus(`Locked: ${data.plate}`);
        setTimeout(() => { stopCamera(); onResult?.(data); }, 650);
      }
    } catch {
      /* ignore transient errors while continuously scanning */
    } finally {
      inFlight.current = false;
    }
  }, [onResult, stopAuto, stopCamera, signalLock]);

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) { inputRef.current?.click(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } }, audio: false });
      streamRef.current = stream;
      attempts.current = 0;
      inFlight.current = false;
      setCamOpen(true);
    } catch {
      toast.message("Camera unavailable — pick a photo instead.");
      inputRef.current?.click();
    }
  };

  // Wire the stream to the video element and start auto-scanning once it's playing.
  useEffect(() => {
    if (!camOpen || !videoRef.current || !streamRef.current) return;
    const v = videoRef.current;
    v.srcObject = streamRef.current;
    const start = () => {
      stopAuto();
      setStatus("Scanning — hold steady on the plate…");
      timerRef.current = setInterval(tick, AUTO_INTERVAL_MS);
    };
    v.play().then(start).catch(() => start());
    return () => stopAuto();
  }, [camOpen, tick, stopAuto]);

  const capture = () => {
    const img = grabFrame();
    if (!img) return;
    stopCamera();
    scan(img);
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { scan(await resizeToDataUrl(file)); }
    catch { toast.error("Could not read that photo."); }
  };

  return (
    <>
      <button type="button" onClick={openCamera} disabled={loading}
        className={className || "btn-ghost h-12 px-4 font-head flex items-center gap-2 disabled:opacity-60"}
        data-testid={testid}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
        {loading ? "Reading…" : label}
      </button>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />

      {camOpen && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-4" data-testid={`${testid}-camera`}>
          <div className="w-full max-w-2xl">
            <div className="flex items-center justify-between mb-3">
              <p className="font-head font-800 uppercase tracking-wide text-white flex items-center gap-2"><ScanLine className="w-5 h-5 text-[#FFB800]" /> Point at the number plate</p>
              <button type="button" onClick={stopCamera} className="text-white/70 hover:text-white p-2" data-testid={`${testid}-close`}><X className="w-6 h-6" /></button>
            </div>
            <div className="relative bg-black border border-white/15 overflow-hidden">
              <video ref={videoRef} playsInline muted className="w-full max-h-[60vh] object-contain" />
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                <div className="border-2 border-[#FFB800]/80 w-3/4 h-1/4 rounded" />
              </div>
              {flash && (
                <div className="pointer-events-none absolute inset-0 bg-[#22C55E]/70 flex items-center justify-center transition-opacity" data-testid={`${testid}-lock-flash`}>
                  <Check className="w-20 h-20 text-white" strokeWidth={3} />
                </div>
              )}
              <div className="pointer-events-none absolute top-2 left-2 flex items-center gap-2 bg-black/70 px-3 py-1.5 rounded" data-testid={`${testid}-status`}>
                <Loader2 className="w-4 h-4 animate-spin text-[#FFB800]" />
                <span className="font-mono text-xs text-white">{status || "Starting camera…"}</span>
              </div>
            </div>
            <p className="font-body text-sm text-white/70 mt-3 text-center">Auto-detecting — just hold the plate in the frame. No need to tap anything.</p>
            <div className="flex gap-3 mt-3">
              <button type="button" onClick={capture} className="btn-ghost flex-1 h-12 font-head flex items-center justify-center gap-2 border border-white/20 text-white" data-testid={`${testid}-capture`}>
                <Camera className="w-5 h-5" /> Capture now
              </button>
              <button type="button" onClick={() => { stopCamera(); inputRef.current?.click(); }} className="h-12 px-4 border border-white/15 text-white/80 font-head flex items-center gap-2 hover:text-white transition-colors" data-testid={`${testid}-upload`}>
                <Upload className="w-4 h-4" /> Upload
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
